<script lang="ts">
  import { page } from '$app/stores';
  import type { SvelteSeo } from '../../ambient';

  export let title: SvelteSeo['title'] = undefined;
  export let description: SvelteSeo['description'] = undefined;
  export let keywords: SvelteSeo['keywords'] = undefined;
  export let applicationName: SvelteSeo['applicationName'] = undefined;
  export let themeColor: SvelteSeo['themeColor'] = undefined;
  export let favicon: SvelteSeo['favicon'] = undefined;

  export let nofollow: SvelteSeo['nofollow'] = false;
  export let noindex: SvelteSeo['noindex'] = false;
  export let nositelinkssearchbox: SvelteSeo['nositelinkssearchbox'] = false;
  export let notranslate: SvelteSeo['notranslate'] = false;

  export let twitter: SvelteSeo['twitter'] = undefined;
  export let og: SvelteSeo['og'] = undefined;
  export let jsonLd: SvelteSeo['jsonLd'] = undefined;

  // Optional: If you support passing a full object instead
  export let meta: Partial<SvelteSeo> = {};
</script>

<svelte:head>
  <link rel="canonical" href="{$page.url.origin}{$page.url.pathname}" />
  <meta property="og:url" content="{$page.url.origin}{$page.url.pathname}" />

  <!-- Title and Description -->
  {#if meta.title || title}
    <title>{meta.title || title}</title>
    <meta property="og:title" content={meta.title || title} />
    <meta name="twitter:title" content={meta.title || title} />
  {/if}

  {#if meta.description || description}
    <meta name="description" content={meta.description || description} />
    <meta property="og:description" content={meta.description || description} />
    <meta name="twitter:description" content={meta.description || description} />
  {/if}

  <!-- Keywords -->
  {#if meta.keywords || keywords}
    <meta name="keywords" content={meta.keywords || keywords} />
  {/if}

  <!-- Open Graph Meta Tags -->
  {#if meta.og || og}
    {#if meta.og?.type || og?.type}
      <meta property="og:type" content={meta.og?.type || og?.type} />
    {/if}
    {#if meta.og?.site_name || og?.site_name}
      <meta property="og:site_name" content={meta.og?.site_name || og?.site_name} />
    {/if}
    {#if meta.og?.locale || og?.locale}
      <meta property="og:locale" content={meta.og?.locale || og?.locale} />
    {/if}
    {#if meta.og?.image || og?.image}
      <meta property="og:image" content={meta.og?.image || og?.image} />
    {/if}
    {#if meta.og?.image_alt || og?.image_alt}
      <meta property="og:image:alt" content={meta.og?.image_alt || og?.image_alt} />
    {/if}
  {/if}

  <!-- Twitter Card -->
  {#if meta.twitter || twitter}
    {#if meta.twitter?.card || twitter?.card}
      <meta name="twitter:card" content={meta.twitter?.card || twitter?.card} />
    {/if}
    {#if meta.twitter?.site || twitter?.site}
      <meta name="twitter:site" content={meta.twitter?.site || twitter?.site} />
    {/if}
    {#if meta.twitter?.creator || twitter?.creator}
      <meta name="twitter:creator" content={meta.twitter?.creator || twitter?.creator} />
    {/if}
  {/if}

  <!-- JSON-LD Schema Markup -->
  {#if meta.jsonLd || jsonLd}
    {@const jsonLdData = Object.assign({ '@context': 'https://schema.org' }, meta.jsonLd || jsonLd)}
    {@html '<script type="application/ld+json">' + JSON.stringify(jsonLdData) + '</script>'}
  {/if}
  <!-- Application and Theme Color -->
  {#if applicationName}
    <meta name="application-name" content={applicationName} />
  {/if}

  {#if themeColor}
    <meta name="theme-color" content={themeColor} />
  {/if}

  <!-- Robots Meta Tag for Indexing and Following -->
  <meta name="robots" content={`${noindex ? 'noindex' : 'index'},${nofollow ? 'nofollow' : 'follow'}`} />
  <meta name="googlebot" content={`${noindex ? 'noindex' : 'index'},${nofollow ? 'nofollow' : 'follow'}`} />

  <!-- Google Sitelinks Searchbox and Translate -->
  {#if nositelinkssearchbox}
    <meta name="google" content="nositelinkssearchbox" />
  {/if}

  {#if notranslate}
    <meta name="google" content="notranslate" />
  {/if}

  <!-- Favicon -->
  {#if favicon}
    <link rel="icon" href={favicon} />
  {/if}
</svelte:head>
